import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";

import Text from "../../components/ui/Text";
import CopyrightFooter from "../../components/copyrightFooter/CopyrightFooter";
import Menu from "../../components/menu/Menu";
import Touchable from "../../components/ui/Touchable";
import useCtaStore from "../../hooks/store/useCtaStore";
import useStore from "../../hooks/store/useStore";
import CaringCta from "../../pages/caringCta/CaringCta";
import DisneyMenu from "../../pages/disneyMenu/DisneyMenu";
import DisneyMenuWeb from "../../pages/disneyMenu/DisneyMenuWeb";
import { mobileMaxWidth, tabletMaxWidth } from "../../constants/layout";
import { appBgColorMapping } from "../../styles/constants";
import cn from "../../utils/cn";
import ctaHandler8, {
  IntervalMsTime,
  hasCtaTimer,
} from "../../utils/ctaHandlers/ctaHandler8";
import ctaHandler12to14 from "../../utils/ctaHandlers/ctaHandler12to14";
import { MenuItemType, SourceType } from "../../types";
import { pageMapping } from "./constants";

export default function HomeLayout({
  selectedMenuItem,
  setSelectedMenuItem,
  showDisneyMenu,
  setShowDisneyMenu,
  contentOverride,
}: {
  selectedMenuItem: MenuItemType;
  setSelectedMenuItem: Dispatch<SetStateAction<MenuItemType>>;
  showDisneyMenu: boolean | null;
  setShowDisneyMenu: Dispatch<SetStateAction<boolean | null>>;
  contentOverride?: React.ReactNode;
}) {
  const { t } = useTranslation();

  const { showingCtaETId, setShowingCtaETId } = useCtaStore(
    (state) => state.cta,
  );
  const {
    ctaIntervalCount,
    setCtaIntervalCount,
    ctaIntervalBlock,
    ctaIntervalDisplayCount,
    setCtaIntervalDisplayCount,
  } = useCtaStore((state) => state.ctaInterval);

  const { source } = useStore((state) => state.global);
  const { showBookDemoDrawer, showBookDemoThanks, showWannaUnlockModel } =
    useStore((state) => state.bookDemo);
  const { showAppDownloadModel } = useStore((state) => state.webVersion);
  const { userInformation } = useStore((state) => state.user);
  const shouldSetTimer = hasCtaTimer(userInformation);

  useEffect(() => {
    if (shouldSetTimer) {
      const handleTouch = () => {
        if (ctaIntervalCount !== 0) setCtaIntervalCount(0);
      };

      window.addEventListener("touchstart", handleTouch);

      return () => {
        window.removeEventListener("touchstart", handleTouch);
      };
    }
  }, [ctaIntervalCount, setCtaIntervalCount, shouldSetTimer]);

  useEffect(() => {
    if (shouldSetTimer) {
      const interval = setInterval(() => {
        ctaHandler8(
          ctaIntervalBlock,
          ctaIntervalCount,
          setCtaIntervalCount,
          showingCtaETId,
          setShowingCtaETId,
          ctaIntervalDisplayCount,
          setCtaIntervalDisplayCount,
          showBookDemoDrawer,
          showBookDemoThanks,
          showWannaUnlockModel,
          showAppDownloadModel,
        );
      }, IntervalMsTime);

      return () => {
        clearInterval(interval);
      };
    }
  }, [
    shouldSetTimer,
    ctaIntervalBlock,
    ctaIntervalCount,
    setCtaIntervalCount,
    showingCtaETId,
    setShowingCtaETId,
    ctaIntervalDisplayCount,
    setCtaIntervalDisplayCount,
    showBookDemoDrawer,
    showBookDemoThanks,
    showWannaUnlockModel,
    showAppDownloadModel,
  ]);

  useEffect(() => {
    ctaHandler12to14(userInformation, setShowingCtaETId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const touchAction =
    showingCtaETId ||
    showBookDemoDrawer ||
    showBookDemoThanks ||
    showWannaUnlockModel ||
    showAppDownloadModel
      ? "touch-none"
      : "";

  if (source === SourceType.DemoApp) return <div>Page Not Found</div>;

  if (showDisneyMenu)
    return (
      <>
        {source === SourceType.ClubApp ? (
          <DisneyMenu
            setSelectedMenuItem={setSelectedMenuItem}
            setShowDisneyMenu={setShowDisneyMenu}
          />
        ) : (
          <DisneyMenuWeb
            setSelectedMenuItem={setSelectedMenuItem}
            setShowDisneyMenu={setShowDisneyMenu}
          />
        )}

        {showingCtaETId ? (
          <CaringCta ctaExperienceTypeId={showingCtaETId} />
        ) : null}
      </>
    );

  const showingPlaylistPage =
    !contentOverride &&
    (selectedMenuItem === "listen" || selectedMenuItem === "bedtime");

  const contentMaxWidth = showingPlaylistPage ? tabletMaxWidth : mobileMaxWidth;

  return (
    <div
      className={cn(
        appBgColorMapping[selectedMenuItem],
        "relative flex min-h-screen w-full flex-col items-center",
      )}
    >
      <div className="relative z-10 h-[122px] w-full overflow-hidden tablet:h-[145px]">
        <div className="pointer-events-none absolute bottom-0 left-1/2 size-[1600px] -translate-x-1/2 rounded-full bg-gradient-to-b from-[#FFF8F2] to-white shadow-[0_0_12px_#FBCDA64D]" />

        <div
          className="relative mx-auto w-full min-w-0"
          style={{ maxWidth: tabletMaxWidth }}
        >
          <div className="relative mb-2.5 flex h-11 items-center justify-center">
            {source === SourceType.Web ? (
              <Touchable
                className="absolute left-1 top-2.5"
                onClick={() => {
                  const queryParams = window.location.search;

                  window.location.href = `/${queryParams}`;
                }}
              >
                <ChevronLeft className="size-6 text-orange" />
              </Touchable>
            ) : null}

            <Text lg>{t("digitalExperience")}</Text>
          </div>

          <div className="relative w-full min-w-0 px-6 pb-[26px]">
            <Menu
              selectedMenuItem={selectedMenuItem}
              setSelectedMenuItem={setSelectedMenuItem}
            />
          </div>
        </div>
      </div>

      <div
        className={cn(
          "relative z-10 flex w-full min-w-[320px] flex-1 flex-col",
          touchAction,
        )}
        style={{ maxWidth: contentMaxWidth }}
      >
        {contentOverride ?? pageMapping[selectedMenuItem]}

        {showingPlaylistPage ? null : <CopyrightFooter className="mt-auto" />}

        {showingCtaETId ? (
          <CaringCta ctaExperienceTypeId={showingCtaETId} />
        ) : null}
      </div>
    </div>
  );
}
